import { ReactNode, useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { TrafficLight } from '@/lib/foco-calculations';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface MetricCardProps {
  title: string;
  value: string;
  subtitle?: ReactNode;
  icon?: ReactNode;
  trafficLight?: TrafficLight;
  className?: string;
  delay?: number;
  trend?: {
    direction: 'up' | 'down' | 'neutral';
    percentage: number;
    isPositive?: boolean; // For metrics where "up" is good (income) vs bad (expenses)
  };
}

export function MetricCard({ 
  title, 
  value, 
  subtitle, 
  icon, 
  trafficLight,
  className,
  delay = 0,
  trend
}: MetricCardProps) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  const trafficColors: Record<TrafficLight, string> = {
    green: 'border-l-success',
    yellow: 'border-l-warning',
    red: 'border-l-destructive',
  };

  const trafficBgColors: Record<TrafficLight, string> = {
    green: 'from-success/5 to-transparent',
    yellow: 'from-warning/5 to-transparent',
    red: 'from-destructive/5 to-transparent',
  };

  const getTrendColor = () => {
    if (!trend || trend.direction === 'neutral') return 'text-muted-foreground';
    const isGood = trend.isPositive !== undefined 
      ? (trend.direction === 'up' ? trend.isPositive : !trend.isPositive)
      : trend.direction === 'up';
    return isGood ? 'text-success' : 'text-destructive';
  };

  const TrendIcon = trend?.direction === 'up' ? TrendingUp : trend?.direction === 'down' ? TrendingDown : Minus;

  return (
    <div 
      className={cn(
        'metric-card metric-card-glow border-l-4 p-3 sm:p-4 opacity-0',
        trafficLight ? trafficColors[trafficLight] : 'border-l-primary',
        trafficLight && `bg-gradient-to-br ${trafficBgColors[trafficLight]}`,
        isVisible && 'animate-slide-up',
        className
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="space-y-0.5 sm:space-y-1 min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <p className="text-xs sm:text-sm font-medium text-muted-foreground truncate">{title}</p>
            {trend && trend.direction !== 'neutral' && (
              <div className={cn('flex items-center gap-0.5 text-[10px] font-semibold', getTrendColor())}>
                <TrendIcon className="h-3 w-3" />
                <span>{Math.abs(trend.percentage) > 999 ? '+999%' : `${Math.abs(trend.percentage).toFixed(0)}%`}</span>
              </div>
            )}
          </div>
          <p className={cn(
            'text-lg sm:text-2xl font-bold tracking-tight truncate',
            isVisible && 'animate-number'
          )}>
            {value}
          </p>
          {subtitle && (
            <p className="text-[10px] sm:text-xs text-muted-foreground truncate">{subtitle}</p>
          )}
        </div>
        {icon && (
          <div className={cn(
            'rounded-xl p-2 sm:p-2.5 flex-shrink-0 transition-transform duration-300 hover:scale-110',
            trafficLight === 'green' && 'bg-success/10 text-success',
            trafficLight === 'yellow' && 'bg-warning/10 text-warning',
            trafficLight === 'red' && 'bg-destructive/10 text-destructive',
            !trafficLight && 'bg-primary/10 text-primary',
          )}>
            {icon}
          </div>
        )}
      </div>
      {trafficLight && (
        <div className="mt-2 sm:mt-3 flex items-center gap-1.5 sm:gap-2">
          <div className={cn(
            'h-2 w-2 rounded-full flex-shrink-0 animate-pulse',
            trafficLight === 'green' && 'bg-success',
            trafficLight === 'yellow' && 'bg-warning',
            trafficLight === 'red' && 'bg-destructive',
          )} />
          <span className={cn(
            'text-[10px] sm:text-xs font-semibold truncate uppercase tracking-wide',
            trafficLight === 'green' && 'text-success',
            trafficLight === 'yellow' && 'text-warning',
            trafficLight === 'red' && 'text-destructive',
          )}>
            {trafficLight === 'green' && 'Saludable'}
            {trafficLight === 'yellow' && 'Atención'}
            {trafficLight === 'red' && 'Crítico'}
          </span>
        </div>
      )}
    </div>
  );
}
